import store from '@/store/BookingStore'
import { BookingFunnelService } from '@/service/BookingFunnelService/BookingFunnelService'
import { Traveller } from '../../../../requests-module'
import { CSEService } from '@/service/CSEService'
import { isCSE } from '@/utils/cse'
import type { Locale } from '@/service/translate/flights/flightTranslations.types'
import {
    computeAdultChildCounts,
    computeTravellerAges,
    getMatchingParticipantsFromLocalStorage,
    SavedParticipantData,
    splitTravellerIndicesByAdultChild,
} from './participantsLocalStorage'

const PLACEHOLDER_STRING = 'participant'

const isTravellerEmpty = (traveller: Traveller): boolean => {
    const firstName = (traveller.firstName || '').trim()
    const lastName = (traveller.lastName || '').trim()
    const lower = PLACEHOLDER_STRING.toLowerCase()

    if (!firstName && !lastName) return true
    if (!traveller.fulfilled) return true
    if (firstName.toLowerCase().includes(lower) || lastName.toLowerCase().includes(lower)) {
        return true
    }
    return false
}

const mergeSavedData = (
    traveller: Traveller,
    saved: SavedParticipantData
): Traveller | null => {
    if (!isTravellerEmpty(traveller)) return null

    const birthday = saved.birthday || traveller.birthday

    return {
        ...traveller,
        firstName: saved.firstName || traveller.firstName,
        lastName: saved.lastName || traveller.lastName,
        birthday,
        gender: saved.gender ?? traveller.gender,
        fulfilled: !!(saved.firstName && saved.lastName && birthday),
    }
}

const mapSavedToCurrentByAdultChild = (
    currentTravellers: Traveller[],
    currentAges: number[],
    savedTravellers: SavedParticipantData[],
    returnDate: string
): { travellers: Traveller[]; hasChanges: boolean } => {
    const savedAges = computeTravellerAges(
        savedTravellers.map((saved, index) => ({
            id: String(index),
            birthday: saved.birthday,
        })),
        returnDate
    )

    const { adultIndices: currentAdults, childIndices: currentChildren } =
        splitTravellerIndicesByAdultChild(currentAges)
    const { adultIndices: savedAdults, childIndices: savedChildren } =
        splitTravellerIndicesByAdultChild(savedAges)

    let hasChanges = false
    const updated = currentTravellers.map((traveller, index) => {
        const adultSlot = currentAdults.indexOf(index)
        const childSlot = currentChildren.indexOf(index)
        let savedIdx: number | undefined
        if (adultSlot >= 0) savedIdx = savedAdults[adultSlot]
        else if (childSlot >= 0) savedIdx = savedChildren[childSlot]

        if (savedIdx === undefined) return traveller
        const merged = mergeSavedData(traveller, savedTravellers[savedIdx])
        if (merged) {
            hasChanges = true
            return merged
        }
        return traveller
    })

    return { travellers: updated, hasChanges }
}

export const prefillParticipantsFromLocalStorage = async (): Promise<void> => {
    const locale = store.getters.getLocale
    if (locale && isCSE(locale as Locale)) {
        const cseService = new CSEService(locale)
        if (cseService.isCSE) {
            cseService.init()
            if (cseService.isCGOS) return
        }
    }

    const booking = store.getters.getBooking
    if (!booking?.travellers?.length || !booking.returnDate) return

    const currentAges = computeTravellerAges(booking.travellers, booking.returnDate)
    const counts = computeAdultChildCounts(currentAges)
    if (!counts) return

    const savedTravellers = getMatchingParticipantsFromLocalStorage(counts)
    if (!savedTravellers) return

    const { travellers: updatedTravellers, hasChanges } = mapSavedToCurrentByAdultChild(
        booking.travellers,
        currentAges,
        savedTravellers,
        booking.returnDate
    )

    if (!hasChanges) return

    await BookingFunnelService.updateParticipants(updatedTravellers)
}
